import { LOGO_VS_LOGO_LEAGUES } from '../data/leagues';
import type { LogoVsLogoTeam } from '../data/types';

export type ColorPickSource = 'default' | 'visitor-swap' | 'local-swap' | 'both-swap' | 'fallback';

export interface PickColorsResult {
  localHex: string;
  visitorHex: string;
  source: ColorPickSource;
}

declare module '../../../../../types/bulk-ops' {
  interface TemplateBulkOps {
    pickColors(leagueId: string, localId: string, visitorId: string): PickColorsResult | undefined;
  }
}

// Un par de colores pasa si tiene bastante distancia RGB *y* hue diferente.
// Solo RGB confundía casos como #E60000 vs #D53C42 (dos rojos, distancia alta).
const MIN_COLOR_DISTANCE = 70;
const MIN_HUE_DIFF = 0.04;
// Colores casi-grises (blanco/negro/gris) no aportan hue confiable; los ignoramos.
const GRAY_SATURATION = 0.1;

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return null;
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

function colorDistance(a: string, b: string): number {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  if (!ca || !cb) return Infinity;
  return Math.sqrt((ca[0] - cb[0]) ** 2 + (ca[1] - cb[1]) ** 2 + (ca[2] - cb[2]) ** 2);
}

function rgbToHsv(rgb: [number, number, number]): { h: number; s: number; v: number } {
  const [r, g, b] = rgb.map(c => c / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const v = max;
  const s = max === 0 ? 0 : d / max;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
    if (h < 0) h += 1;
  }
  return { h, s, v };
}

function hueDiff(a: string, b: string): number {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  if (!ca || !cb) return 1;
  const ha = rgbToHsv(ca);
  const hb = rgbToHsv(cb);
  // Con un color casi-gris, damos por hecho que no choca (retorna el máximo).
  if (ha.s < GRAY_SATURATION || hb.s < GRAY_SATURATION) return 1;
  const d = Math.abs(ha.h - hb.h);
  return Math.min(d, 1 - d);
}

function isSufficientlyDistinct(a: string, b: string): boolean {
  return colorDistance(a, b) >= MIN_COLOR_DISTANCE && hueDiff(a, b) >= MIN_HUE_DIFF;
}

// Devuelve el color para el slot local/visitante, respetando los flags
// visitorOnly/localOnly (una variante puede estar restringida a un solo selector).
function colorForLocal(team: LogoVsLogoTeam, variantIdx: number): string | undefined {
  const v = team.variants[variantIdx];
  if (!v || v.visitorOnly) return undefined;
  return v.hex;
}

function colorForVisitor(team: LogoVsLogoTeam, variantIdx: number): string | undefined {
  const v = team.variants[variantIdx];
  if (!v || v.localOnly) return undefined;
  return v.visitorHex ?? v.hex;
}

// Combinaciones de variantes que probamos, en orden. La primera que dé un par
// suficientemente distinto se elige; si ninguna alcanza, caemos al default (0,0).
const ATTEMPTS: ReadonlyArray<readonly [number, number, ColorPickSource]> = [
  [0, 0, 'default'],
  [0, 1, 'visitor-swap'],
  [1, 0, 'local-swap'],
  [1, 1, 'both-swap']
];

function pickColors(leagueId: string, localId: string, visitorId: string): PickColorsResult | undefined {
  const league = LOGO_VS_LOGO_LEAGUES.find(l => l.value === leagueId);
  const localTeam = league?.teams.find(t => t.value === localId);
  const visitorTeam = league?.teams.find(t => t.value === visitorId);
  if (!localTeam || !visitorTeam) return undefined;

  for (const [li, vi, source] of ATTEMPTS) {
    const localHex = colorForLocal(localTeam, li);
    const visitorHex = colorForVisitor(visitorTeam, vi);
    if (!localHex || !visitorHex) continue;
    if (isSufficientlyDistinct(localHex, visitorHex)) {
      return { localHex, visitorHex, source };
    }
  }

  const localHex = colorForLocal(localTeam, 0);
  const visitorHex = colorForVisitor(visitorTeam, 0);
  if (!localHex || !visitorHex) return undefined;
  return { localHex, visitorHex, source: 'fallback' };
}

export const bulkOperations = { pickColors };
